/**
 * Photos: the page's logo and its gallery (up to 12). Unlike the other
 * cards, every change here is saved as it is made — an upload is already a
 * write — so there is no Save button and nothing for the unsaved-changes
 * guard to hold, except a photo's description while it is being edited.
 *
 * Uploads go through `api.uploadHostedPageAsset` (multipart; the server
 * rotates, resizes to ≤ 1600 px and strips metadata). Errors are the API's:
 * 409 `GALLERY_FULL`, 413 too large, 415 not an image. Order is
 * `sort_order`; Up/Down swap two photos and PATCH the ones whose position
 * changed. Delete asks first; deleting the logo clears it on the profile.
 */
import { useId, useState, type ChangeEvent } from 'react';
import { ApiError } from '../api/errors';
import type { HostedPageAsset, HostedPageAssetKind, HostedPageProfileResponse } from '../api/types';
import { useSession } from '../auth/session';
import { useUnsavedChangesGuard } from '../router';
import { ErrorNotice, Field, useToast } from '../ui/kit';
import { useMutation } from '../ui/hooks';
import { LIMITS } from './model';

type AssetsUpdate = (fn: (prev: HostedPageAsset[]) => HostedPageAsset[]) => void;

export function byOrder(a: HostedPageAsset, b: HostedPageAsset): number {
  return a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at);
}

/** The API's upload refusals, in words, naming the file. */
export function uploadErrorText(err: unknown, fileName: string): string {
  if (err instanceof ApiError) {
    if (err.code === 'GALLERY_FULL') return `Your page already has ${LIMITS.gallery} photos. Delete one to add another.`;
    if (err.status === 413) return `${fileName} is too large. Use a smaller photo (a phone photo resized for email works).`;
    if (err.status === 415) return `${fileName} isn’t a photo we can use. Upload a JPEG, PNG or WebP image.`;
    return `${fileName}: ${err.message}`;
  }
  return `${fileName}: the upload didn’t finish. Try again.`;
}

function Thumb({ asset, label }: { asset: HostedPageAsset; label: string }) {
  return (
    <img
      src={asset.url}
      alt={asset.alt ?? ''}
      title={label}
      width={asset.width || undefined}
      height={asset.height || undefined}
      loading="lazy"
      style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 'var(--r-sm)', background: 'var(--panel-sunk)' }}
    />
  );
}

/** One gallery photo: its description, caption, position and Delete. */
function PhotoCard({
  slug,
  asset,
  index,
  count,
  onMove,
  onSaved,
  onDeleted,
  busy,
}: {
  slug: string;
  asset: HostedPageAsset;
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
  onSaved: (asset: HostedPageAsset) => void;
  onDeleted: (id: string) => void;
  busy: boolean;
}) {
  const { api } = useSession();
  const toast = useToast();
  const name = `Photo ${index + 1}`;
  const [alt, setAlt] = useState(asset.alt ?? '');
  const [caption, setCaption] = useState(asset.caption ?? '');
  const savedKey = JSON.stringify([asset.alt, asset.caption]);
  const [seenKey, setSeenKey] = useState(savedKey);
  if (seenKey !== savedKey) {
    setSeenKey(savedKey);
    setAlt(asset.alt ?? '');
    setCaption(asset.caption ?? '');
  }
  const [confirming, setConfirming] = useState(false);
  const patch = {
    ...(alt.trim() !== (asset.alt ?? '') ? { alt: alt.trim() || null } : {}),
    ...(caption.trim() !== (asset.caption ?? '') ? { caption: caption.trim() || null } : {}),
  };
  const dirty = Object.keys(patch).length > 0;
  useUnsavedChangesGuard(dirty);
  const save = useMutation(() => api.updateHostedPageAsset(slug, asset.id, patch));
  const remove = useMutation(async () => {
    await api.deleteHostedPageAsset(slug, asset.id);
    return true;
  });

  return (
    <li className="sunk stack-sm" aria-label={name} style={{ listStyle: 'none', padding: 12, minWidth: 0 }}>
      <Thumb asset={asset} label={name} />
      {index === 0 ? (
        <p className="small" style={{ margin: 0, fontWeight: 600 }}>
          Cover photo
        </p>
      ) : null}
      <form
        className="stack-sm"
        aria-label={`${name} details`}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!dirty) return;
          const res = await save.run();
          if (res) {
            onSaved(res.asset);
            toast('Photo details saved');
          }
        }}
      >
        <Field label={`${name} description`} optional hint="What the photo shows, for people who can’t see it.">
          {(p) => <input {...p} className="input" value={alt} disabled={save.pending} onChange={(e) => setAlt(e.target.value)} placeholder="New water heater in a garage" />}
        </Field>
        <Field label={`${name} caption`} optional>
          {(p) => <input {...p} className="input" value={caption} disabled={save.pending} onChange={(e) => setCaption(e.target.value)} />}
        </Field>
        {dirty ? (
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <button type="submit" className="btn btn-sm btn-primary" disabled={save.pending}>
              {save.pending ? 'Saving…' : `Save ${name.toLowerCase()} details`}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                setAlt(asset.alt ?? '');
                setCaption(asset.caption ?? '');
                save.reset();
              }}
            >
              Discard
            </button>
          </div>
        ) : null}
        <ErrorNotice error={save.error} />
      </form>
      <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
        <button type="button" className="btn btn-sm btn-ghost" disabled={busy || index === 0} aria-label={`Move ${name.toLowerCase()} earlier`} onClick={() => onMove(index, index - 1)}>
          Earlier
        </button>
        <button type="button" className="btn btn-sm btn-ghost" disabled={busy || index === count - 1} aria-label={`Move ${name.toLowerCase()} later`} onClick={() => onMove(index, index + 1)}>
          Later
        </button>
        {confirming ? null : (
          <button type="button" className="btn btn-sm btn-danger-subtle" aria-label={`Delete ${name.toLowerCase()}`} onClick={() => setConfirming(true)}>
            Delete
          </button>
        )}
      </div>
      {confirming ? (
        <div className="stack-sm" role="group" aria-label={`Delete ${name.toLowerCase()}?`}>
          <p className="small" style={{ margin: 0 }}>
            Delete this photo? It comes off your page within a minute.
          </p>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={remove.pending}
              onClick={async () => {
                if (await remove.run()) {
                  onDeleted(asset.id);
                  toast('Photo deleted');
                }
              }}
            >
              {remove.pending ? 'Deleting…' : 'Delete photo'}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirming(false)}>
              Keep it
            </button>
          </div>
          <ErrorNotice error={remove.error} />
        </div>
      ) : null}
    </li>
  );
}

/** A file picker styled as a button, with a real label (the input stays in the tab order). */
function FilePicker({
  label,
  multiple,
  disabled,
  describedBy,
  onFiles,
}: {
  label: string;
  multiple?: boolean;
  disabled?: boolean;
  describedBy?: string;
  onFiles: (files: File[]) => void;
}) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="file"
        accept="image/*"
        multiple={multiple}
        disabled={disabled}
        aria-describedby={describedBy}
        className="input"
        style={{ padding: 6 }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
    </div>
  );
}

export function PhotosSection({
  slug,
  assets,
  logoAssetId,
  updateAssets,
  onProfileSaved,
  onRefresh,
}: {
  slug: string;
  assets: HostedPageAsset[];
  logoAssetId: string | null;
  updateAssets: AssetsUpdate;
  onProfileSaved: (response: HostedPageProfileResponse) => void;
  /** Re-reads the profile (the completeness score, a cleared logo). */
  onRefresh: () => void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const headingId = useId();
  const reasonId = useId();
  const gallery = assets.filter((a) => a.kind === 'gallery').sort(byOrder);
  const logos = assets.filter((a) => a.kind === 'logo').sort(byOrder);
  const logo = assets.find((a) => a.id === logoAssetId) ?? null;
  const full = gallery.length >= LIMITS.gallery;
  const [progress, setProgress] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState<ApiError | null>(null);
  const setLogo = useMutation((id: string | null) => api.updateHostedPageProfile(slug, { logo_asset_id: id }));
  const removeLogo = useMutation(async (id: string) => {
    await api.deleteHostedPageAsset(slug, id);
    return id;
  });

  const upload = async (files: File[], kind: HostedPageAssetKind): Promise<HostedPageAsset[]> => {
    const done: HostedPageAsset[] = [];
    const errs: string[] = [];
    const room = kind === 'gallery' ? Math.max(0, LIMITS.gallery - gallery.length) : 1;
    const take = files.slice(0, room);
    if (files.length > take.length) {
      errs.push(
        kind === 'gallery'
          ? `Only ${room} more ${room === 1 ? 'photo fits' : 'photos fit'} (a page shows up to ${LIMITS.gallery}), so ${files.length - take.length} ${files.length - take.length === 1 ? 'was' : 'were'} not added.`
          : 'Choose one image for the logo.',
      );
    }
    for (let i = 0; i < take.length; i += 1) {
      const file = take[i] as File;
      if (file.type && !file.type.startsWith('image/')) {
        errs.push(`${file.name} isn’t a photo we can use. Upload a JPEG, PNG or WebP image.`);
        continue;
      }
      setProgress(take.length > 1 ? `Uploading photo ${i + 1} of ${take.length}…` : 'Uploading…');
      try {
        const res = await api.uploadHostedPageAsset(slug, { file, kind });
        done.push(res.asset);
        updateAssets((prev) => [...prev.filter((a) => a.id !== res.asset.id), res.asset]);
      } catch (err) {
        errs.push(uploadErrorText(err, file.name));
        if (err instanceof ApiError && err.code === 'GALLERY_FULL') break;
      }
    }
    setProgress(null);
    setProblems(errs);
    if (done.length) {
      toast(done.length === 1 ? (kind === 'logo' ? 'Logo uploaded' : 'Photo added') : `${done.length} photos added`);
      onRefresh();
    }
    return done;
  };

  const move = async (from: number, to: number) => {
    const order = gallery.slice();
    const [it] = order.splice(from, 1);
    if (!it) return;
    order.splice(to, 0, it);
    const changed = order.map((a, i) => ({ a, i })).filter(({ a, i }) => a.sort_order !== i);
    const before = assets;
    updateAssets((prev) => prev.map((a) => {
      const at = order.findIndex((o) => o.id === a.id);
      return at >= 0 ? { ...a, sort_order: at } : a;
    }));
    setMoving(true);
    setMoveError(null);
    try {
      for (const { a, i } of changed) {
        const res = await api.updateHostedPageAsset(slug, a.id, { sort_order: i });
        updateAssets((prev) => prev.map((x) => (x.id === res.asset.id ? res.asset : x)));
      }
    } catch (err) {
      updateAssets(() => before);
      setMoveError(err instanceof ApiError ? err : new ApiError({ status: 0, code: 'NETWORK_ERROR' }));
    } finally {
      setMoving(false);
    }
  };

  return (
    <section id="hp-photos" className="panel panel-pad stack" aria-labelledby={headingId} tabIndex={-1}>
      <div className="stack-sm" style={{ gap: 4 }}>
        <h2 id={headingId} className="h-sub" style={{ margin: 0 }}>
          Photos
        </h2>
        <p className="small muted" style={{ margin: 0 }}>
          Your logo and photos of your work. Changes here save as you make them. Photos are resized and their location data is removed.
        </p>
      </div>

      <div className="stack-sm" role="group" aria-labelledby={`${headingId}-logo`}>
        <h3 id={`${headingId}-logo`} style={{ margin: 0, fontSize: 14 }}>
          Logo
        </h3>
        {logo ? (
          <div className="row" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ width: 96 }}>
              <img src={logo.url} alt="Your logo" width={logo.width || undefined} height={logo.height || undefined} style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'contain' }} />
            </div>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={setLogo.pending}
              onClick={async () => {
                const res = await setLogo.run(null);
                if (res) {
                  onProfileSaved(res);
                  toast('Logo removed from your page');
                }
              }}
            >
              Stop using this logo
            </button>
          </div>
        ) : (
          <p className="small muted" style={{ margin: 0 }}>
            No logo uploaded: your page shows the logo from your published widget branding, if it has one.
          </p>
        )}
        {logos.filter((l) => l.id !== logoAssetId).length ? (
          <ul className="stack-sm" style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Other logos you uploaded">
            {logos
              .filter((l) => l.id !== logoAssetId)
              .map((l, i) => (
                <li key={l.id} className="row" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ width: 64 }}>
                    <img src={l.url} alt={`Uploaded logo ${i + 1}`} style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'contain' }} />
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={setLogo.pending}
                    aria-label={`Use uploaded logo ${i + 1}`}
                    onClick={async () => {
                      const res = await setLogo.run(l.id);
                      if (res) {
                        onProfileSaved(res);
                        toast('Logo updated');
                      }
                    }}
                  >
                    Use this logo
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger-subtle"
                    disabled={removeLogo.pending}
                    aria-label={`Delete uploaded logo ${i + 1}`}
                    onClick={async () => {
                      const id = await removeLogo.run(l.id);
                      if (id) {
                        updateAssets((prev) => prev.filter((a) => a.id !== id));
                        toast('Logo deleted');
                      }
                    }}
                  >
                    Delete
                  </button>
                </li>
              ))}
          </ul>
        ) : null}
        <FilePicker
          label={logo ? 'Upload a different logo' : 'Upload a logo'}
          disabled={progress !== null || setLogo.pending}
          onFiles={async (files) => {
            const [up] = await upload(files.slice(0, 1), 'logo');
            if (up) {
              const res = await setLogo.run(up.id);
              if (res) onProfileSaved(res);
            }
          }}
        />
        <ErrorNotice error={setLogo.error ?? removeLogo.error} />
      </div>

      <hr className="divider" style={{ margin: 0 }} />

      <div className="stack-sm" role="group" aria-labelledby={`${headingId}-gallery`}>
        <h3 id={`${headingId}-gallery`} style={{ margin: 0, fontSize: 14 }}>
          Photos of your work
        </h3>
        <p className="small muted" style={{ margin: 0 }}>
          Before-and-after shots, your van, your team. Three or more make your page look established.
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          Your first photo is the big picture at the top of your page. Use Earlier and Later to choose it.
        </p>
        <FilePicker label="Add photos" multiple disabled={full || progress !== null} describedBy={reasonId} onFiles={(files) => void upload(files, 'gallery')} />
        <p id={reasonId} className="small muted" style={{ margin: 0 }}>
          {full ? `Your page has ${LIMITS.gallery} of ${LIMITS.gallery} photos, the most it can show. Delete one to add another.` : `${gallery.length} of ${LIMITS.gallery} photos. JPEG, PNG or WebP.`}
        </p>
        <div role="status" aria-live="polite" className="small">
          {progress}
        </div>
        {problems.length ? (
          <div className="notice bad" role="alert">
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {problems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <ErrorNotice error={moveError} />
        {gallery.length ? (
          <ul
            aria-label="Your photos, in the order your page shows them"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))', gap: 12, margin: 0, padding: 0 }}
          >
            {gallery.map((a, i) => (
              <PhotoCard
                key={a.id}
                slug={slug}
                asset={a}
                index={i}
                count={gallery.length}
                busy={moving}
                onMove={(from, to) => void move(from, to)}
                onSaved={(saved) => updateAssets((prev) => prev.map((x) => (x.id === saved.id ? saved : x)))}
                onDeleted={(id) => {
                  updateAssets((prev) => prev.filter((x) => x.id !== id));
                  onRefresh();
                }}
              />
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
